import React from 'react';
import { Upload, Typography, List, Row, Col, Modal, message } from 'antd';
import type { RcFile, UploadChangeParam, UploadFile } from 'antd/es/upload/interface';
import './FileUpload.scss';
import { DeleteOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { FiUpload } from 'react-icons/fi';
import { formatMaxSize, getMaxBytesForExtension, validateShapefileCumulativeSize } from '../../FileManager/constants';

const { Dragger } = Upload;
const { Text } = Typography;

const toUploadFile = (file: RcFile): UploadFile<any> => ({
  uid: file.uid,
  name: file.name,
  size: file.size,
  type: file.type,
  originFileObj: file,
  status: 'done',
  percent: 100,
});

interface FileUploadModalProps {
  folderId: string;
  selectedFiles: UploadFile<any>[];
  handleFileChange: (event: UploadChangeParam<UploadFile<any>>) => void;
  allowedExtensions?: string[];
  validationError?: string;
  onRemove?: (file: UploadFile<any>) => void;
}

const FileUpload: React.FC<FileUploadModalProps> = ({ selectedFiles, handleFileChange, allowedExtensions, validationError, onRemove }) => {
  const [fileList, setFileList] = React.useState<UploadFile<any>[]>(selectedFiles);
  const fileListRef = React.useRef<UploadFile<any>[]>(selectedFiles);

  React.useEffect(() => {
    setFileList(selectedFiles);
    fileListRef.current = selectedFiles;
    return () => {
      setFileList([]);
    };
  }, [selectedFiles]);

  const applyFileChange = React.useCallback(
    (newList: UploadFile<any>[], changedFile: UploadFile<any>) => {
      fileListRef.current = newList;
      setFileList(newList);
      handleFileChange({ file: changedFile, fileList: newList });
    },
    [handleFileChange],
  );

  const clearShapefileFiles = React.useCallback(
    (changedFile: UploadFile<any>) => {
      // Clear the controlled list and the ref together. The ref is the source of
      // truth for individually selected files, so a failed part cannot leave
      // previously selected siblings available for the next validation.
      fileListRef.current = [];
      setFileList([]);
      handleFileChange({ file: changedFile, fileList: [] });
    },
    [handleFileChange],
  );

  const handleRemove = React.useCallback(
    (removeItem: UploadFile<any>) => {
      setFileList((prev) => prev.filter((item) => item.uid !== removeItem.uid));
      onRemove?.(removeItem);
    },
    [onRemove],
  );

  const onDraggerChange = (event: UploadChangeParam<UploadFile<any>>) => {
    // beforeUpload owns shapefile state so cumulative validation also works
    // when the four components are selected in separate actions. Ignore the
    // stale Ant Upload list emitted after a rejected component is cleared.
    if (allowedExtensions?.some((e) => e.toLowerCase() === '.shp')) return;

    handleFileChange(event);
    setFileList(event.fileList);
  };

  const handleBeforeUpload = React.useCallback(
    (file: RcFile): false | typeof Upload.LIST_IGNORE => {
      // Size is gated before the extension check so an unresolved sourceFormat
      // (no allowedExtensions) still enforces a limit.
      const isShapefile = allowedExtensions?.some((e) => e.toLowerCase() === '.shp') ?? false;
      if (!isShapefile) {
        const maxBytes = getMaxBytesForExtension(file.name);
        if (file.size > maxBytes) {
          message.error(`${file.name} exceeds the ${formatMaxSize(maxBytes)} limit.`);
          return Upload.LIST_IGNORE;
        }
      }

      if (!allowedExtensions || allowedExtensions.length === 0) return false;

      const current = fileListRef.current;
      const asFile = toUploadFile(file);
      const extOf = (n: string) => n.slice(n.lastIndexOf('.')).toLowerCase();
      const baseOf = (n: string) => n.slice(0, n.lastIndexOf('.')).toLowerCase();
      const applyShapefileFiles = (nextFiles: UploadFile<any>[], changedFile: UploadFile<any>) => {
        const error = validateShapefileCumulativeSize(nextFiles, allowedExtensions);
        if (error) {
          message.error(error);
          clearShapefileFiles(changedFile);
          return;
        }
        applyFileChange(nextFiles, changedFile);
      };

      if (isShapefile) {
        const fileExt = extOf(file.name);
        if (!allowedExtensions.some((e) => e.toLowerCase() === fileExt)) return Upload.LIST_IGNORE;

        const exactDup = current.find((f) => baseOf(f.name) === baseOf(file.name) && extOf(f.name) === fileExt);
        if (exactDup) {
          Modal.confirm({
            title: 'File already uploaded',
            content: `"${file.name}" is already uploaded. Replace it?`,
            okText: 'Replace',
            cancelText: 'Keep existing',
            onOk: () => {
              const latest = fileListRef.current;
              applyShapefileFiles(latest.filter((f) => f.uid !== exactDup.uid).concat(asFile), asFile);
            },
          });
          return Upload.LIST_IGNORE;
        }

        const slotConflict = current.find((f) => extOf(f.name) === fileExt);
        if (slotConflict) {
          applyShapefileFiles(current.filter((f) => f.uid !== slotConflict.uid).concat(asFile), asFile);
          return Upload.LIST_IGNORE;
        }

        applyShapefileFiles([...current, asFile], asFile);
        return Upload.LIST_IGNORE;
      }

      // Non-shapefile: 1-file limit
      const exactDup = current.find((f) => f.name.toLowerCase() === file.name.toLowerCase());
      if (exactDup) {
        Modal.confirm({
          title: 'File already uploaded',
          content: `"${file.name}" is already uploaded. Replace it?`,
          okText: 'Replace',
          cancelText: 'Keep existing',
          onOk: () => applyFileChange([asFile], asFile),
        });
        return Upload.LIST_IGNORE;
      }

      applyFileChange([asFile], asFile);
      return Upload.LIST_IGNORE;
    },
    [allowedExtensions, applyFileChange, clearShapefileFiles],
  );

  const computeShapefileSlotStatus = (slots: { ext: string; file?: UploadFile<any> }[]): Array<'default' | 'error' | 'success'> => {
    const uploadedSlots = slots.filter((s) => s.file);
    if (uploadedSlots.length === 0) return slots.map(() => 'default');

    const baseOf = (name: string) => {
      const i = name.lastIndexOf('.');
      return i < 0 ? name : name.slice(0, i).toLowerCase();
    };

    const bases = uploadedSlots.map((s) => baseOf(s.file!.name));
    const allSame = bases.every((b) => b === bases[0]);
    if (allSame) return slots.map(() => 'success');

    const counts: Record<string, number> = {};
    for (const b of bases) counts[b] = (counts[b] ?? 0) + 1;
    const majorityBase = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];

    return slots.map((s) => {
      if (!s.file) return 'default';
      return baseOf(s.file.name) === majorityBase ? 'success' : 'error';
    });
  };

  const renderFileSlots = () => {
    if (!allowedExtensions || allowedExtensions.length === 0) {
      return (
        <List
          className="file-list"
          dataSource={fileList}
          renderItem={(item) => (
            <List.Item>
              <Row align="middle" justify="space-between" gutter={[6, 6]}>
                <Col span={22}>
                  <Text ellipsis={{ tooltip: item?.name }}>{item?.name}</Text>
                </Col>
                <Col span={2}>
                  <DeleteOutlined onClick={() => handleRemove(item)} />
                </Col>
              </Row>
            </List.Item>
          )}
        />
      );
    }

    const assignedFiles = new Set<string>();
    const isShapefile = allowedExtensions.some((ext) => ext.toLowerCase() === '.shp');

    if (isShapefile) {
      const rawSlots = allowedExtensions.map((ext) => {
        const file = fileList.find((f) => !assignedFiles.has(f.uid) && f.name.toLowerCase().endsWith(ext.toLowerCase()));
        if (file) assignedFiles.add(file.uid);
        return { ext, file };
      });

      const statuses = computeShapefileSlotStatus(rawSlots);

      const slots = rawSlots.map((s, i) => ({
        ...s,
        status: s.file ? statuses[i] : ((validationError ? 'error' : 'missing') as 'error' | 'missing'),
        key: s.ext,
      }));

      const extraFiles = fileList.filter((f) => !assignedFiles.has(f.uid));

      return (
        <div className="file-upload__slots">
          {slots.map((slot) => (
            <div
              key={slot.key}
              className={[
                'file-upload__slot',
                slot.status === 'missing'
                  ? 'file-upload__slot--missing'
                  : slot.status === 'error'
                    ? 'file-upload__slot--error'
                    : slot.status === 'success'
                      ? 'file-upload__slot--success'
                      : 'file-upload__slot--default',
              ].join(' ')}
            >
              {slot.file ? (
                <>
                  <span title={slot.file.name.toUpperCase()} className="file-upload__slot-text">
                    {slot.file.name.toUpperCase()}
                  </span>
                  <DeleteOutlined className="file-upload__delete-icon" onClick={() => handleRemove(slot.file!)} />
                </>
              ) : (
                `PLEASE UPLOAD ${slot.ext.toUpperCase()} FILE`
              )}
            </div>
          ))}
          {extraFiles.map((file) => (
            <div key={file.uid} className="file-upload__slot file-upload__slot--unexpected">
              <span title={`${file.name.toUpperCase()} (UNEXPECTED)`} className="file-upload__slot-text error">
                {file.name.toUpperCase()} (UNEXPECTED)
              </span>
              <DeleteOutlined className="file-upload__delete-icon" onClick={() => handleRemove(file)} />
            </div>
          ))}
        </div>
      );
    }

    // Non-shapefile: single slot
    const validFiles = fileList.filter(
      (f) => !assignedFiles.has(f.uid) && allowedExtensions.some((ext) => f.name.toLowerCase().endsWith(ext.toLowerCase())),
    );
    validFiles.forEach((f) => assignedFiles.add(f.uid));
    const extraFiles = fileList.filter((f) => !assignedFiles.has(f.uid));
    const labelExtensions = allowedExtensions.map((e) => e.toUpperCase()).join(' OR ');

    return (
      <div className="file-upload__slots">
        {validFiles.length > 0 ? (
          validFiles.map((f) => (
            <div key={f.uid} className="file-upload__slot file-upload__slot--success">
              <span title={f.name.toUpperCase()} className="file-upload__slot-text">
                {f.name.toUpperCase()}
              </span>
              <DeleteOutlined className="file-upload__delete-icon" onClick={() => handleRemove(f)} />
            </div>
          ))
        ) : (
          <div className={`file-upload__slot ${validationError ? 'file-upload__slot--error' : 'file-upload__slot--missing'}`}>
            {`PLEASE UPLOAD ${labelExtensions} FILE`}
          </div>
        )}
        {extraFiles.map((file) => (
          <div key={file.uid} className="file-upload__slot file-upload__slot--unexpected">
            <span title={`${file.name.toUpperCase()} (UNEXPECTED)`} className="file-upload__slot-text error">
              {file.name.toUpperCase()} (UNEXPECTED)
            </span>
            <DeleteOutlined className="file-upload__delete-icon" onClick={() => handleRemove(file)} />
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="upload-container">
      <Row gutter={[12, 12]} align="top">
        <Col span={12}>
          <div className="file-list-container">{renderFileSlots()}</div>
        </Col>
        <Col span={12}>
          <div className="upload-area">
            <Dragger
              multiple
              fileList={fileList}
              onChange={onDraggerChange}
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              openFileDialogOnClick={false}
              accept={allowedExtensions ? allowedExtensions.join(',') : undefined}
              className="file-upload__dragger"
            >
              <FiUpload className="file-upload__dragger-icon" />
              <p className="file-upload__dragger-text">DRAG AND DROP FILES</p>
            </Dragger>
            <Upload
              multiple
              fileList={fileList}
              onChange={onDraggerChange}
              beforeUpload={handleBeforeUpload}
              showUploadList={false}
              accept={allowedExtensions ? allowedExtensions.join(',') : undefined}
              className="file-upload__select-upload"
            >
              <button type="button" className="file-upload__select-btn">
                SELECT FILES LOCALLY
              </button>
            </Upload>
          </div>
        </Col>
      </Row>
      {validationError && (
        <div className="file-upload__error-banner">
          <CloseCircleOutlined className="file-upload__error-icon" />
          <div className="file-upload__error-body">
            <span className="file-upload__error-title">Validation Failed</span>
            <span className="file-upload__error-message">{validationError}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
